
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import MainLayout from '@/components/layout/MainLayout';
import SubscriptionForm from '@/components/subscriptions/SubscriptionForm';
import { useSubscriptionForm } from '@/hooks/useSubscriptionForm';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';

const NewSubscription = () => {
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const { handleSubmit, isSubmitting } = useSubscriptionForm();
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const { translate } = useLanguage();
  
  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      toast.error(translate("subscriptions.authRequired"));
      navigate('/auth');
    }
  }, [user, isLoading, navigate, translate]);

  const handleFormSubmit = async (data: any) => {
    console.log("NewSubscription: Form data received:", data);
    
    // Check authentication
    if (!user) {
      toast.error(translate("subscriptions.authRequired"));
      navigate('/auth');
      return;
    }
    
    // Clear previous errors
    setFormErrors([]);
    
    // Basic validation
    const errors: string[] = [];
    
    // Check customer selection
    if (!data.customer_id) {
      errors.push(translate("subscriptions.invalidCustomer"));
    }
    
    // Check services only if they are present in the form
    if (data.services) {
      const selectedServices = data.services.filter((service: any) => service.selected);
      if (!selectedServices || selectedServices.length === 0) {
        errors.push(translate("subscriptions.invalidService"));
      }
    }
    
    // Check installation fields
    if (!data.installation_street) errors.push(translate("subscriptions.streetRequired"));
    if (!data.installation_zip_code) errors.push(translate("subscriptions.zipCodeRequired"));
    if (!data.installation_city) errors.push(translate("subscriptions.cityRequired"));
    
    // If we have errors, display them and stop submission
    if (errors.length > 0) {
      setFormErrors(errors);
      errors.forEach(error => toast.error(error));
      return;
    }

    try {
      // Filter services before sending them if they exist
      if (data.services) {
        data.services = data.services.filter((service: any) => service.selected);
        
        // Make sure all services have a valid price
        data.services = data.services.map((service: any) => {
          // Use default price if price is null, undefined or empty
          if (service.price === null || service.price === undefined || service.price === '') {
            // Get the default price from the service
            const serviceInfo = data.services.find((s: any) => s.service_id === service.service_id);
            return {
              ...service,
              price: serviceInfo?.default_price || 0
            };
          }
          return service;
        });
      }
      
      console.log("Subscription data being sent:", data);
      
      const result = await handleSubmit(data);
      if (result) {
        toast.success(translate('subscriptions.createdSuccessfully'));
        navigate('/subscriptions');
      }
    } catch (error) {
      console.error('Error in subscription flow:', error);
      toast.error(translate('subscriptions.error'));
    }
  };

  // If authentication is loading, show loading state
  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <p>{translate('common.loading')}</p>
        </div>
      </MainLayout>
    );
  }

  // If not authenticated, don't render the form
  if (!user) {
    return null;
  }

  return (
    <MainLayout>
      <div className="mb-6">
        <Link to="/subscriptions" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-4 w-4 mr-1" />
          {translate('common.back')} {translate('common.to')} {translate('navigation.subscriptions')}
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold">{translate('subscriptions.newSubscription')}</h1>
        <p className="text-muted-foreground">{translate('subscriptions.createNewSubscription')}</p>
      </div>

      {formErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          <p className="font-medium">{translate('subscriptions.formErrors')}</p>
          <ul className="list-disc pl-5 mt-2">
            {formErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <SubscriptionForm
        onSubmit={handleFormSubmit}
        isLoading={isSubmitting}
        allowedStatuses={["registered", "active"]}
        disableNewCustomer={true}
      />
    </MainLayout>
  );
};

export default NewSubscription;
